import React, { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Fab,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  TrendingUp as TrendingUpIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { creditCardOperations, CreditCard } from '../db/database';
import CreditCardForm from './CreditCardForm';

const CreditCardsTab: React.FC = () => {
  const creditCards = useLiveQuery(() => creditCardOperations.getAllCards());
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CreditCard | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [payoffMonths, setPayoffMonths] = useState<{ [key: number]: number }>({});

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, card: CreditCard) => {
    setAnchorEl(event.currentTarget);
    setSelectedCard(card);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setFormOpen(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (selectedCard?.id) {
      if (window.confirm(`Are you sure you want to delete ${selectedCard.name}?`)) {
        await creditCardOperations.deleteCard(selectedCard.id);
      }
    }
    handleMenuClose();
  };

  const handleSave = async (card: Omit<CreditCard, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (selectedCard?.id) {
      await creditCardOperations.updateCard(selectedCard.id, card);
    } else {
      await creditCardOperations.addCard(card);
    }
  };

  const handleAddNew = () => {
    setSelectedCard(null);
    setFormOpen(true);
  };

  const calculatePayoffInfo = (card: CreditCard, months: number = 12) => {
    const monthlyPayment = creditCardOperations.calculatePayoffPlan(
      card.balance,
      card.interestRate,
      months
    );
    const { totalInterest, months: actualMonths } = creditCardOperations.calculateTotalInterest(
      card.balance,
      card.interestRate,
      monthlyPayment
    );
    
    return { monthlyPayment, totalInterest, actualMonths };
  };

  const getPayoffMonths = (cardId: number | undefined) => {
    if (!cardId) return 12;
    return payoffMonths[cardId] || 12;
  };

  const setPayoffMonthsForCard = (cardId: number | undefined, months: number) => {
    if (!cardId) return;
    setPayoffMonths(prev => ({ ...prev, [cardId]: months }));
  };

  if (!creditCards) {
    return (
      <Box p={2}>
        <Typography>Loading credit cards...</Typography>
      </Box>
    );
  }

  if (creditCards.length === 0) {
    return (
      <Box p={2}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No credit cards added yet. Click the + button to add your first card and track payoff progress!
        </Alert>
        
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
          <CreditCardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Track Your Credit Cards
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add your credit cards to calculate payoff plans, track interest, and manage your debt effectively.
          </Typography>
        </Paper>

        <Fab
          color="primary"
          aria-label="add"
          onClick={handleAddNew}
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
        >
          <AddIcon />
        </Fab>

        <CreditCardForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSave={handleSave}
          card={selectedCard}
        />
      </Box>
    );
  }

  const totalBalance = creditCards.reduce((sum, card) => sum + card.balance, 0);

  return (
    <Box>
      {/* Summary Card */}
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(240, 147, 251, 0.3)',
      }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.25)',
            borderRadius: '50%',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CreditCardIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.95, mb: 0.5, fontWeight: 600 }}>
              Total Credit Card Debt
            </Typography>
            <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-1px' }}>
              £{totalBalance.toFixed(2)}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Across {creditCards.length} {creditCards.length === 1 ? 'card' : 'cards'}
        </Typography>
      </Paper>

      {/* Credit Cards List */}
      <Box>
        {creditCards.map((card) => {
          const months = getPayoffMonths(card.id);
          const { monthlyPayment, totalInterest, actualMonths } = calculatePayoffInfo(card, months);
          const isPaymentTooLow = actualMonths === Infinity;

          return (
            <Card key={card.id} sx={{ 
              mb: 2, 
              border: '1px solid', 
              borderColor: 'divider',
              borderLeft: '4px solid',
              borderLeftColor: 'error.main',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2.5}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                      <Box sx={{
                        bgcolor: 'error.light',
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <CreditCardIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700}>{card.name}</Typography>
                    </Box>
                    
                    <Typography variant="h4" color="error.main" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.5px' }}>
                      £{card.balance.toFixed(2)}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={`${card.interestRate}% APR`}
                        size="small"
                        sx={{ 
                          bgcolor: 'warning.light',
                          color: 'warning.contrastText',
                          fontWeight: 700
                        }}
                      />
                      {card.minimumPayment && (
                        <Chip
                          label={`Min: £${card.minimumPayment.toFixed(2)}/mo`}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Stack>
                  </Box>

                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, card)}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Payoff Calculator */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Payoff Plan
                  </Typography>
                  
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <Typography variant="body2">Pay off in:</Typography>
                    {[6, 12, 24, 36].map((m) => (
                      <Chip
                        key={m}
                        label={`${m}mo`}
                        size="small"
                        onClick={() => setPayoffMonthsForCard(card.id, m)}
                        color={months === m ? 'primary' : 'default'}
                        variant={months === m ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Stack>

                  {!isPaymentTooLow ? (
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Monthly Payment:
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={600}>
                          £{monthlyPayment.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                          <TrendingUpIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Total Interest:
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={600} color="warning.main">
                          £{totalInterest.toFixed(2)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Total Paid:
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          £{(card.balance + totalInterest).toFixed(2)}
                        </Typography>
                      </Box>

                      {card.minimumPayment && monthlyPayment < card.minimumPayment && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Monthly payment is below minimum (£{card.minimumPayment.toFixed(2)})
                        </Alert>
                      )}
                    </Stack>
                  ) : (
                    <Alert severity="error">
                      Payment too low to cover interest! Increase payoff period or make larger payments.
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleAddNew}
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
      >
        <AddIcon />
      </Fab>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Form Dialog */}
      <CreditCardForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedCard(null);
        }}
        onSave={handleSave}
        card={selectedCard}
      />
    </Box>
  );
};

export default CreditCardsTab;
